import { testimonialCardVariants, VariantId } from "./presets";
import { extractStyles } from "./utils";
import { Testimonial } from "@/types";
import { LayoutTypes } from "./types";
import { StarRating } from "@/components/atoms/rating";
import { Fragment } from "react/jsx-runtime";
import { Show } from "@/components/atoms/show";
import { CSSProperties } from "react";
import {
  MarqueeGalleryCustomProps,
  MasonryGalleryCustomProps,
  TestimonialCardCustomProps,
} from "@/features/testimonial-gallery/api/types";
import { LoadFonts } from "@/components/atoms/load-fonts";

interface Props {
  isUsedInParent?: boolean;
  data: Testimonial;
  variant: VariantId;
  wrapperStyles?: CSSProperties;
  properties?:
    | TestimonialCardCustomProps
    | MasonryGalleryCustomProps
    | MarqueeGalleryCustomProps;
}

export function GenericTestimonialCard({
  isUsedInParent,
  data: testimonial,
  variant,
  properties,
  wrapperStyles = {},
}: Props) {
  const selectedFont =
    // Testimonial card config
    // @ts-expect-error union props may differ across widgets
    properties?.branding?.fontFamily ||
    // Gallery-level config
    // @ts-expect-error union props may differ across widgets
    properties?.font ||
    "Roboto";

  const baseStyles = testimonialCardVariants[variant];
  const styles = {
    ...baseStyles,
    name: { ...baseStyles.name, fontFamily: selectedFont },
    bio: { ...baseStyles.bio, fontFamily: selectedFont },
    content: { ...baseStyles.content, fontFamily: selectedFont },
    date: { ...baseStyles.date, fontFamily: selectedFont },
  };

  const {
    cardStyles,
    avatarStyles,
    nameStyles,
    bioStyles,
    contentStyles,
    dateStyles,
  } = extractStyles(styles);

  const fontFamily = selectedFont;

  const renderMedia = () => {
    if (!testimonial.media || testimonial.media.length === 0) {
      return null;
    }

    const media = testimonial.media[0];

    if (media.type === "video") {
      return (
        <div
          style={{
            position: "relative",
            width: "100%",
            paddingTop: "56.25%",
            marginBottom: "1rem",
            borderRadius: cardStyles.borderRadius,
            overflow: "hidden",
          }}
        >
          <video
            src={media.url}
            controls
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
      );
    }

    // Handle image media
    if (media.type === "image") {
      return (
        <div
          style={{
            width: "100%",
            marginBottom: "1rem",
            borderRadius: cardStyles.borderRadius,
            overflow: "hidden",
          }}
        >
          <img
            src={media.url}
            alt="Testimonial media"
            style={{ width: "100%", height: "auto", objectFit: "cover" }}
          />
        </div>
      );
    }

    return null;
  };

  const renderRating = () => {
    if (!testimonial.rating || !properties?.visibilityOptions?.showRatings)
      return null;
    return (
      <div style={{ display: "flex", gap: "2px", marginBottom: "0.1rem" }}>
        <StarRating value={testimonial.rating} />
      </div>
    );
  };

  const renderSourceIcon = (type: Testimonial["source"]) => (
    <Fragment>
      {properties?.visibilityOptions?.showSource ? (
        <img
          height={20}
          width={20}
          alt={type}
          src={`/icons/${type}.png`}
          style={{
            position: "absolute",
            top: "1rem",
            right: "1rem",
            flexShrink: 0,
            objectFit: "contain",
          }}
        />
      ) : null}
    </Fragment>
  );

  return (
    <div
      style={{
        width: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        height: "100%",
      }}
    >
      {!isUsedInParent && <LoadFonts font={fontFamily} />}
      <div style={{ ...cardStyles, position: "relative", ...wrapperStyles }}>
        {renderSourceIcon(testimonial.source)}
        <div
          style={{
            display: "flex",
            flexDirection:
              styles["card"].layout == LayoutTypes.CENTERED ? "column" : "row",
            alignItems: "center",
            gap:
              styles["card"].layout == LayoutTypes.CENTERED ? "0.5rem" : "1rem",
          }}
        >
          {styles.avatar.visibility !== "hidden" && (
            <img
              src={testimonial.customer.avatar || styles.avatar.defaultAvatar}
              alt={testimonial.customer.name}
              style={avatarStyles}
            />
          )}
          <div>
            <h3 style={nameStyles}>{testimonial.customer.name}</h3>
            <p
              style={bioStyles}
              className="testify-max-w-[300px] testify-truncate"
            >
              {testimonial.customer.title || testimonial.customer.username}
            </p>
          </div>
        </div>

        {renderRating()}
        <p
          style={contentStyles}
          dangerouslySetInnerHTML={{ __html: testimonial.content }}
        />
        <Show when={!!properties?.visibilityOptions?.showAssets}>
          {renderMedia()}
        </Show>
        {styles.date.visibility &&
          testimonial.date &&
          properties?.visibilityOptions?.showDate && (
            <p style={dateStyles}>
              {new Date(testimonial.date).toLocaleDateString()}
            </p>
          )}
      </div>
    </div>
  );
}
